import type { ComponentType, FC } from "react";

import { Box, HistoryIcon, NullIcon, SuccessIcon } from "@hightouch/htui";

import type { AmpWorkflowStatusFragment } from "src/graphql";
import {
  CATEGORY_COLORS,
  statusCategory,
  type TaskState,
  statusColor,
} from "src/pages/projects/utils";

type Props = {
  status: Pick<AmpWorkflowStatusFragment, "category" | "color"> | null;
  /** Draws a category rather than a status, for cross-workflow groups, or archived. */
  category?: TaskState;
  size?: 3 | 3.5 | 4;
};

// Each category has its own glyph, so where a task stands doesn't rest on color alone.
const CATEGORY_GLYPHS: Partial<Record<TaskState, ComponentType>> = {
  backlog: NullIcon,
  review: HistoryIcon,
  done: SuccessIcon,
};

export const StatusIcon: FC<Props> = ({ status, category, size = 4 }) => {
  const resolvedCategory: TaskState = category ?? statusCategory(status);
  const color = category ? CATEGORY_COLORS[category] : statusColor(status);
  const Glyph = CATEGORY_GLYPHS[resolvedCategory];

  // Paint goes on fill and stroke, not color: a button colors every svg inside it, as IconButton does.
  if (Glyph) {
    return (
      <Box as={Glyph} aria-hidden boxSize={size} flexShrink={0} fill={color} />
    );
  }
  if (resolvedCategory === "active") {
    return (
      <Box
        as="svg"
        aria-hidden
        viewBox="0 0 24 24"
        boxSize={size}
        flexShrink={0}
        fill="none"
        stroke={color}
      >
        <circle cx="12" cy="12" r="9" strokeWidth="2" />
      </Box>
    );
  }
  return (
    <Box
      as="svg"
      aria-hidden
      viewBox="0 0 24 24"
      boxSize={size}
      flexShrink={0}
      fill={color}
    >
      <circle cx="12" cy="12" r="10" />
      <path
        d="M8.5 8.5 15.5 15.5M15.5 8.5 8.5 15.5"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Box>
  );
};

const categoryIcon =
  (category: TaskState): FC =>
  () => <StatusIcon status={null} category={category} />;

// Menus take an icon component, so each category gets one that holds still.
export const CATEGORY_ICONS: Record<TaskState, FC> = {
  backlog: categoryIcon("backlog"),
  active: categoryIcon("active"),
  review: categoryIcon("review"),
  done: categoryIcon("done"),
  archived: categoryIcon("archived"),
};

type ColorDotProps = { color: string; size?: 2 | 2.5 };

export const ColorDot: FC<ColorDotProps> = ({ color, size = 2 }) => (
  <Box
    aria-hidden
    flexShrink={0}
    borderRadius="full"
    bg={color}
    boxSize={size}
  />
);

const colorDotIcons = new Map<string, FC>();

/** A select accessory icon for a color. It paints its own background, so a control's icon colors can't override it. */
export function colorDotIcon(color: string): FC {
  let icon = colorDotIcons.get(color);
  if (!icon) {
    icon = () => <ColorDot color={color} size={2.5} />;
    colorDotIcons.set(color, icon);
  }
  return icon;
}
